import { PALETTE_KEYS, type Palette, type PaletteInput } from './contracts';

/** Bound once to existing TownPropPalette roles. Wood stays wood in both presets. */
export const PALETTES: Readonly<Record<string, Palette>> = {
  coastal_salt: {
    primary: '#647b66', shade: '#293c3e', roof: '#3d5962', roofEdge: '#849b94',
    wood: '#594c3b', woodEdge: '#a18a61', stone: '#627873', iron: '#2b4145',
    glass: '#527e86', glassCore: '#a9c7c4',
  },
  moor_grey: {
    primary: '#65725a', shade: '#283532', roof: '#374c4e', roofEdge: '#929b87',
    wood: '#51473b', woodEdge: '#9b8764', stone: '#667b76', iron: '#273c3b',
    glass: '#607f83', glassCore: '#b5c8bc',
  },
};
export function resolvePalette(id: string, input: PaletteInput = {}): Palette {
  if (!Object.prototype.hasOwnProperty.call(PALETTES, id)) throw new Error(`Unknown paletteId: ${id}`);
  if (!input || typeof input !== 'object' || Array.isArray(input)) throw new Error('Palette must be an object');
  const result = { ...PALETTES[id] };
  for (const [key, value] of Object.entries(input)) {
    if (!(PALETTE_KEYS as readonly string[]).includes(key)) throw new Error(`Unknown palette role: ${key}`);
    if (typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value)) result[key as keyof Palette] = value.toLowerCase();
    else if (typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 0xffffff) {
      result[key as keyof Palette] = '#' + value.toString(16).padStart(6, '0');
    } else throw new Error(`Invalid RGB color for ${key}`);
  }
  return Object.freeze(result);
}
/** Named channels isolate tiny material variation from silhouette and other families. */
export function randomAt(seed: number, channel: string, index: number): number {
  let state = (seed ^ Math.imul(index + 1, 0x9e3779b9)) >>> 0;
  for (let i = 0; i < channel.length; i++) state = Math.imul(state ^ channel.charCodeAt(i), 16777619) >>> 0;
  state ^= state >>> 16; state = Math.imul(state, 0x7feb352d); state ^= state >>> 15;
  return (state >>> 0) / 4294967296;
}
export const n = (value: number): string => Number(value.toFixed(3)).toString();
export const path = (d: string, fill: string, extra = ''): string => `<path d="${d}" fill="${fill}" ${extra}/>`;
export const line = (d: string, color: string, width: number, opacity = 1): string =>
  path(d, 'none', `stroke="${color}" stroke-width="${width}" opacity="${opacity}" stroke-linecap="round" stroke-linejoin="round"`);
export const scaled = (body: string, width: number, height: number, baseW: number, baseH: number): string =>
  `<g transform="scale(${n(width / baseW)} ${n(height / baseH)})">${body}</g>`;

/** Stable semantic scope for defs in composed documents; callers may also provide an instance scope. */
export function semanticScope(value: unknown): string {
  const canonical = (v: unknown): string => {
    if (Array.isArray(v)) return '[' + v.map(canonical).join(',') + ']';
    if (v && typeof v === 'object') return '{' + Object.entries(v).sort(([a], [b]) => a.localeCompare(b))
      .map(([k, item]) => JSON.stringify(k) + ':' + canonical(item)).join(',') + '}';
    return JSON.stringify(v);
  };
  let hash = 14695981039346656037n;
  for (const code of canonical(value)) hash = BigInt.asUintN(64, (hash ^ BigInt(code.charCodeAt(0))) * 1099511628211n);
  return hash.toString(16).padStart(16, '0');
}
